import { jsxs, jsx } from '@emotion/react/jsx-runtime';
import { useTheme, keyframes } from '@emotion/react';
import { createPolymorphicComponent, useSlot } from '@nettbureau/core';
import { Center } from '@nettbureau/layout';
import { useCss, parseFillColor } from '@nettbureau/styling';
import { useSpring } from '@react-spring/core';
import { animated } from '@react-spring/web';
import { isString } from 'radash';
import * as R from 'ramda';

/**
 * CSS animation to shrink and grow the stroke of a circle.
 */ const shrinkAndGrow = (circumference)=>keyframes({
        '0%': {
            strokeDashoffset: circumference - 1,
            transform: 'rotate(0)'
        },
        '50%': {
            strokeDashoffset: circumference / 4,
            transform: 'rotate(45deg)'
        },
        '100%': {
            strokeDashoffset: circumference - 1,
            transform: 'rotate(360deg)'
        }
    });
/**
 * The progress spinners are circular indicators of progress and activity. By default, progress
 * circles inherits the current text color.
 */ const ProgressCircle = createPolymorphicComponent((props, ref)=>{
    const [mergedProps, mergedRef] = useSlot('ProgressCircle', props, ref);
    const { 'aria-label': ariaLabel = 'Loading', as: Component = 'div', children, className, color = 'inherit', css, size = 'md' } = mergedProps;
    const value = props.value ? R.clamp(0, 1, props.value) : null;
    const indeterminate = value === null;
    const { rotate } = useSpring({
        from: {
            rotate: 0
        },
        rotate: 360,
        config: {
            duration: 2000
        },
        loop: true
    });
    const theme = useTheme();
    const diameter = (isString(size) ? theme.heights[size] : size * theme.scales.base) * 0.7;
    const radius = diameter / 2;
    const strokeWidth = diameter / 10;
    const circumference = 2 * (radius - strokeWidth / 2) * Math.PI;
    const containerCss = useCss({
        position: 'relative'
    });
    const svgCss = useCss({
        display: 'block'
    });
    const svgGroupCss = useCss({
        transformOrigin: 'center'
    });
    const circleCss = useCss(({ theme, mixins })=>[
            mixins.transition({
                properties: [
                    'stroke',
                    'strokeDashoffset'
                ],
                duration: 'slow'
            }),
            {
                cx: radius,
                cy: radius,
                r: radius - strokeWidth / 2,
                stroke: color === 'inherit' ? 'currentColor' : parseFillColor(theme, color, 'soft'),
                strokeDasharray: circumference,
                strokeLinecap: 'round',
                transform: 'rotate(-90deg)',
                transformOrigin: 'center'
            },
            indeterminate ? {
                animation: `${shrinkAndGrow(circumference)} 1.4s ease-in-out infinite both`
            } : {
                strokeDashoffset: value && circumference - value * circumference
            },
            css
        ]);
    const backgroundCircleCss = useCss(()=>[
            circleCss,
            {
                stroke: color === 'inherit' ? 'red' : parseFillColor(theme, color, 'soft')
            },
            {
                strokeDasharray: circumference * 2
            }
        ]);
    return /*#__PURE__*/ jsxs(Component, {
        "data-progress-circle": true,
        ref: mergedRef,
        "aria-label": ariaLabel,
        className: className,
        css: containerCss,
        children: [
            /*#__PURE__*/ jsx("svg", {
                css: svgCss,
                fill: "none",
                height: diameter,
                viewBox: `0 0 ${diameter} ${diameter}`,
                width: diameter,
                children: /*#__PURE__*/ jsxs(animated.g, {
                    css: svgGroupCss,
                    style: indeterminate ? {
                        rotate
                    } : {},
                    children: [
                        !indeterminate && /*#__PURE__*/ jsx("circle", {
                            css: backgroundCircleCss,
                            role: "presentation",
                            strokeWidth: strokeWidth
                        }),
                        /*#__PURE__*/ jsx("circle", {
                            css: circleCss,
                            role: "presentation",
                            strokeWidth: strokeWidth
                        })
                    ]
                })
            }),
            /*#__PURE__*/ jsx(Center, {
                css: {
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                    position: 'absolute'
                },
                children: children
            })
        ]
    });
});

export { ProgressCircle };
//# sourceMappingURL=progress-circle.js.map
